/**
 * <vv-memory> — On-device shopper memory (localStorage only; nothing leaves the browser).
 *
 * Modes (attribute mode="..."):
 *   record — Saves a product view. Attributes: handle, concern (collection handle, optional).
 *   rail   — Shows a recently viewed row. Child [data-memory-item][data-handle] items start hidden;
 *            matched ones are shown in most-recent order. Optional exclude="<handle>".
 *            Reveals its closest [data-memory-region] once at least `min` (default 1) items match.
 *            The first item's [data-memory-first] badge is unhidden.
 *   sort   — Moves children of [data-memory-list] whose data-concern matches the saved concern to the front.
 */
const VIEWED_KEY = 'vv:viewed';
const CONCERN_KEY = 'vv:concern';
const MAX_VIEWED = 12;

function read(key, fallback) {
  try {
    const value = JSON.parse(localStorage.getItem(key));
    return value ?? fallback;
  } catch {
    return fallback;
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Storage blocked (private mode); the page works without memory. */
  }
}

class VvMemory extends HTMLElement {
  connectedCallback() {
    const mode = this.getAttribute('mode');
    if (mode === 'record') this.#record();
    else if (mode === 'rail') this.#rail();
    else if (mode === 'sort') this.#sort();
  }

  #record() {
    const handle = this.getAttribute('handle');
    if (!handle) return;
    const viewed = read(VIEWED_KEY, []).filter((h) => typeof h === 'string' && h !== handle);
    viewed.unshift(handle);
    write(VIEWED_KEY, viewed.slice(0, MAX_VIEWED));
    const concern = this.getAttribute('concern');
    if (concern) write(CONCERN_KEY, concern);
  }

  #rail() {
    const exclude = this.getAttribute('exclude');
    const min = Number(this.getAttribute('min') || 1);
    const viewed = read(VIEWED_KEY, []).filter((h) => h !== exclude);
    const items = new Map();
    this.querySelectorAll('[data-memory-item]').forEach((item) => items.set(item.dataset.handle, item));
    const list = this.querySelector('[data-memory-list]');
    let shown = 0;
    for (const handle of viewed) {
      const item = items.get(handle);
      if (!item) continue;
      item.hidden = false;
      list?.append(item);
      if (shown === 0) item.querySelector('[data-memory-first]')?.removeAttribute('hidden');
      shown += 1;
    }
    items.forEach((item) => {
      if (item.hidden) item.remove();
    });
    if (shown >= min) {
      const region = this.closest('[data-memory-region]');
      if (region) region.hidden = false;
      window.dispatchEvent(new Event('resize'));
    }
  }

  #sort() {
    const concern = read(CONCERN_KEY, null);
    const list = this.querySelector('[data-memory-list]');
    if (!concern || !list) return;
    const matches = [...list.children].filter((child) => child.dataset.concern === concern);
    matches.reverse().forEach((child) => list.prepend(child));
  }
}

if (!customElements.get('vv-memory')) customElements.define('vv-memory', VvMemory);
